(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else if (!root.MayLondonStylist) {
    root.MayLondonStylist = factory();
    root.MayLondonStylist.start(root.document);
  }
})(typeof globalThis === 'object' ? globalThis : this, function () {
  'use strict';
  const STORAGE_KEY = 'may-london-v19-stylist-size-v1';
  const EVENT_NAME = 'maylondon:stylist-preferences';
  const NOTES_LIMIT = 500, SIZE_LIMIT = 24;
  const STYLES = Object.freeze(['classic', 'minimal', 'relaxed', 'elegant', '']);
  const OCCASIONS = Object.freeze(['everyday', 'work', 'dinner', 'celebration', '']);
  const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];
  const instances = new WeakMap(), documents = new WeakSet();
  const safeSize = value => typeof value === 'string' && value.length <= SIZE_LIMIT && /^[\p{L}\p{N}\s./-]*$/u.test(value) ? value.trim() : null;
  const cleanNotes = value => typeof value === 'string' ? value.slice(0, NOTES_LIMIT) : '';

  function readSize(storage) {
    try {
      const raw = storage?.getItem(STORAGE_KEY);
      if (!raw || raw.length > 200) return {size: '', error: false};
      const data = JSON.parse(raw);
      const size = data?.version === 1 && data?.optIn === true ? safeSize(data.size) : null;
      return {size: size || '', error: false};
    } catch (_) { return {size: '', error: true}; }
  }

  function mount(root) {
    if (instances.has(root)) return instances.get(root);
    const doc = root.ownerDocument, win = doc.defaultView;
    const strings = JSON.parse(root.querySelector('[data-stylist-strings]').textContent);
    const t = key => strings[key] || '';
    const find = name => root.querySelector('[data-stylist-' + name + ']');
    const node = (tag, text, className) => {
      const el = doc.createElement(tag);
      if (text != null) el.textContent = text;
      if (className) el.className = className;
      return el;
    };
    const button = (key, action, className) => {
      const el = node('button', t(key), className); el.type = 'button';
      el.dataset.stylistAction = key; el.addEventListener('click', action); return el;
    };
    let storage;
    try { storage = win.localStorage; } catch (_) { /* Storage can be blocked independently of JavaScript. */ }
    let stored = '', step = 'notes', answers, remember = false, reuseResolved = false, source = 'none', accepted = false;
    const status = key => { find('status').textContent = key ? t(key) : ''; };
    const updateDelete = () => { find('delete').hidden = !stored; };
    const updateStorage = () => { const value = readSize(storage); stored = value.size; updateDelete(); return value.error; };
    const fresh = (focus = true) => {
      step = 'notes'; answers = {notes: '', style: '', occasion: '', size: ''};
      remember = false; reuseResolved = false; source = 'none'; accepted = false;
      const failed = updateStorage(); render(focus); status(failed ? 'storage_error' : '');
    };
    function forget() {
      try {
        if (!storage) throw new Error('storage');
        storage.removeItem(STORAGE_KEY);
        stored = ''; remember = false;
        if (source === 'stored') { answers.size = ''; source = 'none'; }
        if (step === 'reuse') { reuseResolved = true; step = 'size'; }
        accepted = false; render(); updateDelete(); status('deleted');
      } catch (_) { status('storage_error'); }
    }
    const go = next => { step = next; accepted = false; status(''); render(); };
    function heading(form, key) {
      const h = node('h3', t(key), 'v19-stylist__heading'); h.tabIndex = -1; h.dataset.stylistFocus = ''; form.append(h);
    }
    function field(form, key, control, hint) {
      const label = node('label', t(key), 'v19-stylist__label');
      control.id = root.id + '-' + control.name; label.htmlFor = control.id;
      const group = node('div'); group.append(label, control);
      if (hint) {
        const help = node('p', t(hint), 'v19-stylist__hint'); help.id = control.id + '-help';
        control.setAttribute('aria-describedby', help.id); group.append(help);
      }
      form.append(group); return control;
    }
    function notesInput(form) {
      const control = node('textarea'); control.name = 'notes'; control.maxLength = NOTES_LIMIT;
      control.rows = 3; control.value = answers.notes; control.placeholder = t('notes_placeholder');
      control.addEventListener('input', () => { control.value = cleanNotes(control.value); answers.notes = control.value; changed(); });
      return field(form, 'notes', control, 'notes_help');
    }
    function changed() { accepted = false; status(''); const submit = find('form').querySelector('[type="submit"]'); if (submit) submit.disabled = false; }
    function choices(form, name, options, key) {
      const group = node('fieldset'), legend = node('legend', t(key));
      legend.tabIndex = -1; legend.dataset.stylistFocus = ''; group.append(legend);
      const list = node('div', null, 'v19-stylist__choices');
      options.forEach(value => {
        const label = node('label', null, 'v19-stylist__choice'), radio = node('input');
        radio.type = 'radio'; radio.name = name; radio.value = value; radio.checked = answers[name] === value;
        radio.addEventListener('change', () => { answers[name] = value; changed(); });
        label.append(radio, node('span', t(value || 'no_preference'))); list.append(label);
      });
      group.append(list); form.append(group);
    }
    function sizeInput(form) {
      const control = node('input'); control.type = 'text'; control.name = 'size';
      control.maxLength = SIZE_LIMIT; control.value = answers.size; control.autocomplete = 'off'; control.placeholder = t('size_placeholder');
      const input = field(form, 'size', control, 'size_help');
      input.addEventListener('input', () => {
        answers.size = input.value.slice(0, SIZE_LIMIT); source = answers.size.trim() ? 'entered' : 'none';
        form.querySelectorAll('[name="size_choice"]').forEach(radio => { radio.checked = radio.value === input.value; }); changed();
      });
      const group = node('fieldset'), legend = node('legend', t('size'));
      const list = node('div', null, 'v19-stylist__choices');
      SIZES.forEach(value => {
        const label = node('label', null, 'v19-stylist__choice'), radio = node('input'); radio.type = 'radio'; radio.name = 'size_choice'; radio.value = value; radio.checked = answers.size === value;
        radio.addEventListener('change', () => { input.value = value; answers.size = value; source = 'entered'; changed(); });
        label.append(radio, node('span', value)); list.append(label);
      });
      group.append(legend, list); form.append(group);
      const label = node('label', null, 'v19-stylist__remember'), checkbox = node('input');
      checkbox.type = 'checkbox'; checkbox.name = 'remember'; checkbox.checked = remember;
      checkbox.addEventListener('change', () => { remember = checkbox.checked; changed(); });
      label.append(checkbox, node('span', t('remember'))); form.append(label);
      return input;
    }
    function select(form, name, values) {
      const el = node('select'); el.name = name;
      values.forEach(value => { const option = node('option', t(value || 'no_preference')); option.value = value; el.append(option); });
      el.value = answers[name]; el.addEventListener('change', () => { answers[name] = el.value; changed(); });
      field(form, name, el); return el;
    }
    function actions(form, previous, key = 'continue') {
      const row = node('div', null, 'v19-stylist__actions');
      if (previous) {
        const back = button('back', () => go(previous), 'v19-stylist__icon'); back.textContent = '';
        back.title = t('back'); back.setAttribute('aria-label', t('back'));
        back.append(find('back-icon').content.cloneNode(true)); row.append(back);
      }
      const submit = node('button', t(key), 'v19-stylist__primary'); submit.type = 'submit'; submit.disabled = accepted;
      row.append(submit); form.append(row);
    }
    function valid() {
      if (answers.notes.length > NOTES_LIMIT) { status('too_long'); return false; }
      if (!STYLES.includes(answers.style) || !OCCASIONS.includes(answers.occasion)) { status('invalid_choice'); return false; }
      if (safeSize(answers.size) === null) { status('invalid_size'); return false; }
      answers.size = safeSize(answers.size); return true;
    }
    function confirm() {
      if (accepted || !valid()) return;
      let storageFailed = false;
      if (remember && answers.size) {
        try {
          if (!storage) throw new Error('storage');
          storage.setItem(STORAGE_KEY, JSON.stringify({version: 1, optIn: true, size: answers.size}));
          stored = answers.size; updateDelete();
        } catch (_) { storageFailed = true; }
      }
      // Canceling the event acknowledges that the host has accepted the handoff.
      const detail = Object.freeze({
        version: 1, source: 'v19-stylist', instanceId: root.id, locale: root.dataset.locale || doc.documentElement.lang,
        mode: 'guide', preferences: Object.freeze({...answers}), notesInterpreted: false,
        consent: Object.freeze({useInBuilder: true, reusedStoredSize: source === 'stored', service: false, shareSizeWithService: false})
      });
      const event = new win.CustomEvent(EVENT_NAME, {bubbles: true, cancelable: true, detail});
      root.dispatchEvent(event);
      accepted = event.defaultPrevented;
      render(false); status(accepted ? 'confirmed' : 'builder_unavailable');
      if (storageFailed) find('status').textContent += ' ' + t('storage_error');
    }
    function transcript() {
      const container = find('conversation'); container.replaceChildren();
      if (step === 'notes' || step === 'recap') return;
      const fields = step === 'style' ? ['notes'] : step === 'occasion' ? ['notes', 'style'] : ['notes', 'style', 'occasion'];
      fields.forEach(key => {
        const row = node('dl', null, 'v19-stylist__answer');
        row.append(node('dt', t(key)), node('dd', key === 'notes' ? answers.notes || t('notes_empty') : t(answers[key] || 'no_preference')));
        container.append(row);
      });
    }
    function render(focus = true) {
      const progress = {notes: 1, style: 2, occasion: 3, reuse: 4, size: 4, recap: 5};
      find('progress').textContent = t('step').replace('{current}', String(progress[step])).replace('{total}', '5');
      root.dataset.stylistStep = step; transcript();
      const form = node('form'); form.autocomplete = 'off';
      form.addEventListener('submit', event => {
        event.preventDefault();
        if (step === 'notes') { answers.notes = cleanNotes(form.elements.notes.value); go('style'); }
        else if (step === 'style') go('occasion');
        else if (step === 'occasion') {
          updateStorage(); go(stored && !reuseResolved ? 'reuse' : 'size');
        } else if (step === 'size') { if (valid()) go('recap'); }
        else if (step === 'recap') confirm();
      });
      if (step === 'notes') { notesInput(form); actions(form); }
      if (step === 'style') { choices(form, 'style', STYLES, 'style_question'); actions(form, 'notes'); }
      if (step === 'occasion') { choices(form, 'occasion', OCCASIONS, 'occasion_question'); actions(form, 'style'); }
      if (step === 'reuse') {
        heading(form, 'reuse_question'); form.append(node('p', stored, 'v19-stylist__saved-size'));
        const row = node('div', null, 'v19-stylist__actions');
        row.append(button('use_size', () => {
          answers.size = stored; source = 'stored'; reuseResolved = true; go('recap');
        }, 'v19-stylist__primary'), button('another_size', () => {
          answers.size = ''; source = 'none'; reuseResolved = true; go('size');
        }), button('delete_size', forget)); form.append(row);
      }
      if (step === 'size') {
        heading(form, 'size_question'); sizeInput(form);
        form.append(button('skip_size', () => { answers.size = ''; source = 'none'; remember = false; go('recap'); }));
        actions(form, 'occasion');
      }
      if (step === 'recap') {
        heading(form, 'recap'); form.append(node('p', t('recap_help'), 'v19-stylist__hint'));
        notesInput(form); select(form, 'style', STYLES); select(form, 'occasion', OCCASIONS); sizeInput(form);
        actions(form, 'size', 'confirm');
      }
      find('form').replaceChildren(form); updateDelete();
      if (focus) (form.querySelector('[data-stylist-focus]') || form.querySelector('textarea, input, select, button'))?.focus();
    }
    const onRestart = () => fresh(), onDelete = () => forget();
    const onStorage = event => {
      if (event.key !== STORAGE_KEY && event.key !== null) return;
      const previous = stored; updateStorage();
      if (source === 'stored' && previous !== stored) {
        answers.size = ''; source = 'none'; reuseResolved = false; accepted = false;
        if (['recap', 'reuse', 'size'].includes(step)) step = stored ? 'reuse' : 'size';
      }
      if (step === 'reuse') { if (!stored) step = 'size'; render(false); }
      else if (previous !== stored && !answers.size) render(false);
    };
    find('restart').hidden = false; find('restart').addEventListener('click', onRestart);
    find('delete').addEventListener('click', onDelete); win.addEventListener('storage', onStorage);
    const controller = {
      restart: onRestart,
      destroy() {
        find('restart').removeEventListener('click', onRestart); find('delete').removeEventListener('click', onDelete);
        win.removeEventListener('storage', onStorage); find('form').replaceChildren(); find('conversation').replaceChildren();
        answers = {notes: '', style: '', occasion: '', size: ''}; instances.delete(root);
      }
    };
    instances.set(root, controller); fresh(false); return controller;
  }

  function start(doc) {
    const scan = target => {
      if (target.matches?.('[data-v19-stylist]')) mount(target);
      target.querySelectorAll('[data-v19-stylist]').forEach(mount);
    };
    scan(doc);
    if (documents.has(doc)) return;
    documents.add(doc);
    doc.addEventListener('shopify:section:load', event => scan(event.target));
    doc.addEventListener('shopify:section:unload', event => {
      doc.querySelectorAll('[data-v19-stylist]').forEach(root => { if (event.target.contains(root)) instances.get(root)?.destroy(); });
    });
    doc.defaultView.addEventListener('pageshow', event => {
      if (event.persisted) doc.querySelectorAll('[data-v19-stylist]').forEach(root => instances.get(root)?.restart());
    });
  }

  // Deliberately unconnected to the guide. Enabling this requires a real service
  // and a host consent screen; there is no default URL, discovery or auto-send.
  function createEndpointAdapter({enabled = false, endpoint = '', origin = '', fetch: send} = {}) {
    let target;
    try {
      if (!enabled || !endpoint) throw new Error('disabled');
      const base = new URL(origin); target = new URL(endpoint, base);
      if (!['https:', 'http:'].includes(base.protocol) || target.origin !== base.origin || target.username || target.password || target.hash || target.search) throw new Error('origin');
    } catch (_) { target = null; }
    return Object.freeze({
      available: Boolean(target && typeof send === 'function'),
      async request(preferences, consent = {}, {signal, locale = ''} = {}) {
        if (!target || typeof send !== 'function') throw new Error('service_unavailable');
        if (consent.sendPreferences !== true) throw new Error('service_consent');
        const size = safeSize(preferences?.size || '');
        if (size === null) throw new Error('invalid_size');
        if (size && consent.shareSize !== true) throw new Error('size_consent');
        if (!STYLES.includes(preferences?.style) || !OCCASIONS.includes(preferences?.occasion)) throw new Error('invalid_choice');
        const payload = {version: 1, locale: String(locale).slice(0, 20), preferences: {notes: cleanNotes(preferences.notes), style: preferences.style, occasion: preferences.occasion}};
        if (size) payload.preferences.size = size;
        const response = await send(target.href, {
          method: 'POST', mode: 'same-origin', credentials: 'omit', redirect: 'error', cache: 'no-store', referrerPolicy: 'no-referrer',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(payload), signal: signal || globalThis.AbortSignal?.timeout(12000)
        });
        if (!response.ok || response.redirected) throw new Error('service_failed');
        const data = await response.json();
        if (data?.source !== 'ai' || typeof data.message !== 'string' || !data.message.trim() || data.message.length > 4000) throw new Error('service_failed');
        return Object.freeze({source: 'ai', message: data.message});
      }
    });
  }
  return Object.freeze({mount, start, createEndpointAdapter, readSize, STORAGE_KEY, EVENT_NAME, NOTES_LIMIT, SIZE_LIMIT, STYLES, OCCASIONS});
});
